/**
 * Pannello "Proprieta" del contesto (prototipo `Centro Ticket.dc.html`
 * L334-L359), esteso al dominio reale dalle righe di `propertiesModel.ts`.
 *
 * Ogni modifica passa dalle API gia esistenti, con gli stessi toast del
 * dettaglio attuale. Sul 409 il ticket e cambiato sotto le mani
 * dell'operatore: avviso e rilettura, come `TicketDetail.tsx:202-210`.
 *
 * Parita: "Cambia stato + Applica" (`TicketDetail.tsx:555-579`), "Aggiungi
 * componente" con la modale `AssigneesModal`, trasferimento con motivo
 * obbligatorio dietro il permesso `supporttickets.transfer` (ruling 12).
 *
 * Veste v4 (T37, `Centro Ticket v4.dc.html` L519-L561): stesse righe e stesse
 * chiamate, markup diverso solo dove il design lo chiede — STATO con glifo ed
 * etichetta e sotto la select + Applica; CANALE con swatch e bottone
 * «Trasferisci» (stesso `onOpenTransfer`, senza destinazione: la sceglie la
 * modale/pannello); «+ componente» (nome accessibile invariato); assegnati e
 * tag in sola lettura come chip senza X (CT-115) invece del testo.
 *
 * Parte A: sezione «In copia» (aggiungi/togli/rendi responsabile); sul ticket
 * nello smistamento «Rendi responsabile» apre il trasferimento
 * (onOpenTransfer), perché lo smistamento non scende in copia.
 */
import React, { useEffect, useState } from 'react';
import { UserPlus, X } from 'lucide-react';
import { useToast } from '../../../contexts/ToastContext';
import AssigneesModal from '../AssigneesModal';
import InviteParticipantModal from '../InviteParticipantModal';
import { MAX_TAGS, MAX_TAG_LEN, buildCopyGroupsSection, buildParticipantsSection, buildPropertyRows, normalizeTagInput, type S3PropertyRow } from './propertiesModel';
import { PRIORITY_LABELS, STATUS_LABELS, initialsOf } from '../constants';
import { S3Avatar, S3Badge, S3Modal } from './primitives';
import { photoUrl } from '../../../utils/photoUrl';
import { useSupportLook } from '../v4/lookContext';
import StatusGlyph from '../v4/StatusGlyph';
import { displayStatus } from '../displayStatus';
import DerivedStatusMark from '../DerivedStatusMark';
import ChannelSwatch from '../v4/ChannelSwatch';
import type {
  Assignee, AssigneeRef, Candidate, CopyGroupRef, GroupLite, OperatorSupportApi, TicketDetail,
} from '../../../services/supportApi';

export interface PropertiesCardProps {
  ticket: TicketDetail;
  api: OperatorSupportApi;
  candidates: Candidate[];
  groups: GroupLite[];
  canHandle: boolean;
  canTransfer: boolean;
  /**
   * Ricerca di operatori fuori dal canale nella modale componenti
   * (`supporttickets.assign_external`, Task 26). Default false.
   */
  canAssignExternal?: boolean;
  onChanged: () => void | Promise<void>;
  /** Il cambio canale E un trasferimento: apre la modale col motivo (ruling 12). */
  onOpenTransfer: (toGroupId: string) => void;
  /** Errori diversi dal 409 (401/403/500): li gestisce il contenitore. */
  onActionError: (e: unknown) => void;
}

const PropertiesCard: React.FC<PropertiesCardProps> = ({
  ticket, api, candidates, groups, canHandle, canTransfer, canAssignExternal = false, onChanged, onOpenTransfer, onActionError,
}) => {
  const toast = useToast();
  const v4 = useSupportLook() === 'v4';
  const [busy, setBusy] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [tagInput, setTagInput] = useState<string | null>(null);
  const [assigneesOpen, setAssigneesOpen] = useState(false);
  // Popup «Invita nel ticket» (parte B): un solo stato di apertura, il popup
  // ha un id fisso e non deve mai esistere due volte.
  const [inviteOpen, setInviteOpen] = useState(false);
  // Promozione a responsabile (parte A): conferma in una modale.
  const [promote, setPromote] = useState<CopyGroupRef | null>(null);
  /**
   * Lista normalizzata restituita da `api.setTags`: e la verita del backend
   * (trim, minuscolo, taglio, deduplica) e va mostrata subito, senza aspettare
   * che `onChanged` riporti il ticket. Si azzera appena il ticket ricaricato
   * arriva davvero.
   */
  const [savedTags, setSavedTags] = useState<string[] | null>(null);
  useEffect(() => { setSavedTags(null); }, [ticket]);
  const tags = savedTags || ticket.tags || [];

  const rows = buildPropertyRows(ticket, {
    canHandle,
    canTransfer,
    canEditPriority: Boolean(api.setPriority),
    canEditTags: Boolean(api.setTags),
    groups,
    isGroupMember: ticket.viewerIsMember !== false,
    viewerRole: ticket.viewerRole,
  });
  const searchExternal =
    canAssignExternal && ticket.viewerIsMember !== false && api.searchExternalCandidates
      ? (q: string) => api.searchExternalCandidates!(ticket._id, q)
      : undefined;
  // Partecipanti invitati (parte B).
  const part = buildParticipantsSection(ticket, {
    canAdd: Boolean(api.addParticipants && api.searchInvitable),
    canRemove: Boolean(api.removeParticipant),
  });
  // Canali in copia (parte A).
  const copies = buildCopyGroupsSection(ticket, {
    canTransfer,
    groups,
    hasApi: Boolean(api.addCopyGroups && api.removeCopyGroup && api.setResponsible),
  });

  /**
   * Esegue la chiamata e ne gestisce l'esito. Sul 409 il ticket e cambiato
   * altrove: avviso e rilettura invece dell'errore secco.
   */
  const run = async (fn: () => Promise<void>, okMessage: string) => {
    setBusy(true);
    try {
      await fn();
      toast.success(okMessage);
      await onChanged();
    } catch (err: any) {
      if (err?.response?.status === 409) {
        toast.warning(err.response?.data?.error || 'Il ticket è cambiato: ricarico i dati aggiornati');
        await onChanged();
      } else {
        onActionError(err);
      }
    } finally {
      setBusy(false);
    }
  };

  const saveTags = (next: string[]) =>
    run(async () => { setSavedTags(await api.setTags!(ticket._id, next)); }, 'Tag aggiornati');

  const applyStatus = () => {
    if (!newStatus) return;
    const label = STATUS_LABELS[newStatus] || newStatus;
    setNewStatus('');
    run(async () => { await api.setStatus(ticket._id, newStatus); }, `Stato aggiornato: ${label}`);
  };

  /** Il tag si salva SOLO con Invio: il blur chiude l'input senza scrivere. */
  const commitTag = () => {
    const t = normalizeTagInput(tagInput || '');
    setTagInput(null);
    if (!t) return;
    if (tags.includes(t)) { toast.info('Tag già presente'); return; }
    if (tags.length >= MAX_TAGS) { toast.warning(`Massimo ${MAX_TAGS} tag per ticket`); return; }
    saveTags([...tags, t]);
  };

  /** Select «Cambia stato…» + Applica: identica nelle due vesti. */
  const statusControl = (r: S3PropertyRow) => (
    <span className="s3-prop__status">
      <select
        className="s3-sel s3-sel--prop"
        aria-label="Cambia stato"
        value={newStatus}
        disabled={busy}
        onChange={(e) => setNewStatus(e.target.value)}
      >
        {/* Opzione neutra: lo stato corrente e gia nel badge dell'header,
            qui servono solo le destinazioni (STATUS_TRANSITIONS). */}
        <option value="">Cambia stato…</option>
        {r.options!
          .filter((o) => o.value !== ticket.status)
          .map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <button
        type="button"
        className="s3-pill s3-pill--sm"
        disabled={busy || !newStatus}
        onClick={applyStatus}
      >
        Applica
      </button>
    </span>
  );

  /** Pillola di un assegnatario (avatar, nome, badge Esterno); la X solo se rimovibile. */
  const assigneePill = (a: AssigneeRef, removable: boolean) => {
    const inner = (
      <>
        {/* Avatar sempre presente, stessa misura: foto per i rappresentanti,
            iniziali per gli utenti admin. Le pillole restano allineate. */}
        <S3Avatar initials={initialsOf(a.nome)} size={16} src={photoUrl(a.foto)} title={a.nome} />
        {a.nome}
        {a.external && <S3Badge tone="vio" small title="Esterno al canale: vede solo questo ticket">Esterno</S3Badge>}
      </>
    );
    const key = `${a.actorType}:${a.actorId}`;
    const title = a.external ? `${a.nome} · Esterno al canale` : a.nome;
    if (!removable) {
      return <span key={key} className="s3-pill s3-pill--sm s4-prop__ro" title={title}>{inner}</span>;
    }
    return (
      <button
        key={key}
        type="button"
        className="s3-pill s3-pill--sm"
        disabled={busy}
        aria-label={`Rimuovi ${a.nome}`}
        title={title}
        onClick={() => run(
          async () => {
            await api.setAssignees(ticket._id, {
              remove: [{ actorType: a.actorType, actorId: a.actorId } as Assignee],
            });
          },
          'Componenti aggiornati'
        )}
      >
        {inner}
        <X size={11} aria-hidden="true" />
      </button>
    );
  };

  /** v4: righe che il design mostra con glifo/swatch/chip invece del solo testo. */
  const v4ReadOnly = (r: S3PropertyRow) => {
    if (r.key === 'stato') {
      return (
        <span className="s4-prop__state">
          {displayStatus(ticket).derived ? (
            <DerivedStatusMark d={displayStatus(ticket)} />
          ) : (
            <>
              <StatusGlyph status={ticket.status} decorative />
              <span className="s3-ellipsis">{STATUS_LABELS[ticket.status] || ticket.status}</span>
            </>
          )}
        </span>
      );
    }
    if (r.key === 'canale') {
      const nome = ticket.groupId?.nome || '';
      return (
        <span className="s4-prop__chan">
          <ChannelSwatch channel={ticket.groupId} size={9} />
          <span className="s3-prop__val s3-ellipsis" title={nome || undefined}>{nome || '—'}</span>
        </span>
      );
    }
    if (r.key === 'assegnato' && r.kind === 'text') {
      const list = (ticket.assignees || []).filter((a) => a.nome);
      if (list.length === 0) return <span className="s3-prop__val">nessuno</span>;
      return <div className="s3-prop__chips">{list.map((a) => assigneePill(a, false))}</div>;
    }
    if (r.key === 'tag' && r.kind === 'text') {
      if (tags.length === 0) return <span className="s3-prop__val">—</span>;
      return (
        <div className="s3-prop__chips">
          {tags.map((t) => <span key={t} className="s3-pill s3-pill--sm s4-prop__ro s4-prop__tag" title={t}>{t}</span>)}
        </div>
      );
    }
    return null;
  };

  const control = (r: S3PropertyRow) => {
    if (v4) {
      const ro = v4ReadOnly(r);
      if (ro) return ro;
    }

    if (r.kind === 'text') {
      // Il valore puo essere piu largo della colonna (292px): il testo
      // completo resta raggiungibile col title.
      return <span className="s3-prop__val s3-ellipsis" title={r.value || undefined}>{r.value || '—'}</span>;
    }

    if (r.kind === 'select' && r.key === 'stato') return statusControl(r);

    if (r.kind === 'select' && r.key === 'priorita') {
      return (
        <select
          className="s3-sel s3-sel--prop"
          aria-label="Priorità"
          value={r.value}
          disabled={busy}
          onChange={(e) => {
            const p = e.target.value;
            run(async () => { await api.setPriority!(ticket._id, p); }, `Priorità aggiornata: ${PRIORITY_LABELS[p] || p}`);
          }}
        >
          {r.options!.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      );
    }

    if (r.kind === 'select') {
      // CANALE: scegliere una destinazione non trasferisce da solo, apre la
      // modale col motivo obbligatorio (ruling 12).
      return (
        <select
          className="s3-sel s3-sel--prop"
          aria-label="Canale"
          value=""
          disabled={busy}
          onChange={(e) => { if (e.target.value) onOpenTransfer(e.target.value); }}
        >
          <option value="">{ticket.groupId?.nome || '—'}</option>
          {r.options!.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      );
    }

    if (r.kind === 'assignees') {
      const assignees = ticket.assignees || [];
      return (
        <div className="s3-prop__chips">
          {assignees.map((a) => assigneePill(a, true))}
          {assignees.length === 0 && <span className="s3-prop__val">nessuno</span>}
          {v4 ? (
            // «+ componente» (L550): il nome accessibile resta quello di oggi.
            <button
              type="button"
              className="s3-pill s3-pill--sm s3-prop__add"
              disabled={busy}
              aria-label="Aggiungi componente"
              onClick={() => setAssigneesOpen(true)}
            >
              + componente
            </button>
          ) : (
            <button
              type="button"
              className="s3-pill s3-pill--sm s3-prop__add"
              disabled={busy}
              onClick={() => setAssigneesOpen(true)}
            >
              <UserPlus size={11} aria-hidden="true" /> Aggiungi componente
            </button>
          )}
        </div>
      );
    }

    // TAG: chip removibili + input con validazione client.
    return (
      <div className="s3-prop__chips">
        {tags.map((t) => (
          <button
            key={t}
            type="button"
            className="s3-pill s3-pill--sm"
            disabled={busy}
            aria-label={`Rimuovi tag ${t}`}
            title={t}
            onClick={() => saveTags(tags.filter((x) => x !== t))}
          >
            {t} <X size={10} aria-hidden="true" />
          </button>
        ))}
        {tagInput === null ? (
          <button
            type="button"
            className="s3-pill s3-pill--sm s3-prop__add"
            disabled={busy}
            onClick={() => setTagInput('')}
          >
            + tag
          </button>
        ) : (
          <input
            className="s3-sel s3-prop__taginput"
            autoFocus
            value={tagInput}
            maxLength={MAX_TAG_LEN}
            aria-label="Nuovo tag"
            disabled={busy}
            onChange={(e) => setTagInput(e.target.value)}
            onBlur={() => setTagInput(null)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') { setTagInput(null); return; }
              if (e.key === 'Enter') { e.preventDefault(); commitTag(); }
            }}
          />
        )}
      </div>
    );
  };

  return (
    <div className="s3-card s3-prop" aria-busy={busy || undefined}>
      <span className="s3-h3">Proprietà</span>

      {rows.map((r) => {
        const top = r.kind === 'tags' || r.kind === 'assignees'
          || (v4 && r.kind === 'text' && (r.key === 'assegnato' || r.key === 'tag'));
        const row = (
          <div key={r.key} className={`s3-prop__row${top ? ' s3-prop__row--top' : ''}`}>
            <span className="s3-label s3-prop__k">{r.label}</span>
            {control(r)}
            {/* v4 CANALE: il cambio canale e un bottone, non una select (L537).
                Stessa callback di oggi: in v4 aprira il pannello rapido (T38). */}
            {v4 && r.key === 'canale' && r.kind === 'select' && (
              <button
                type="button"
                className="s3-pill s3-pill--sm s4-prop__xfer"
                disabled={busy}
                onClick={() => onOpenTransfer('')}
              >
                Trasferisci
              </button>
            )}
          </div>
        );
        // v4 STATO: glifo + etichetta sulla riga, select + Applica sotto (L522-L530).
        if (v4 && r.key === 'stato' && r.kind === 'select') {
          return (
            <React.Fragment key={r.key}>
              {row}
              <div className="s4-prop__sub">{statusControl(r)}</div>
            </React.Fragment>
          );
        }
        return row;
      })}

      {part.visible && (
        <div className="s3-prop__row s3-prop__row--top">
          <span className="s3-label s3-prop__k">PARTECIPANTI</span>
          <div className="s3-prop__chips">
            {part.items.map((p) => {
              const key = `${p.actorType}:${p.actorId}`;
              const title = `${p.nome} · Invitato nel ticket`;
              const inner = (
                <>
                  <S3Avatar initials={initialsOf(p.nome)} size={16} title={p.nome} />
                  {p.nome}
                </>
              );
              return part.canRemove ? (
                <button
                  key={key}
                  type="button"
                  className="s3-pill s3-pill--sm"
                  disabled={busy}
                  aria-label={`Togli ${p.nome} dai partecipanti`}
                  title={title}
                  onClick={() => run(
                    async () => { await api.removeParticipant!(ticket._id, p.actorType, p.actorId); },
                    'Partecipante tolto'
                  )}
                >
                  {inner}
                  <X size={11} aria-hidden="true" />
                </button>
              ) : (
                <span key={key} className="s3-pill s3-pill--sm s4-prop__ro" title={title}>{inner}</span>
              );
            })}
            {part.items.length === 0 && <span className="s3-prop__val">nessuno</span>}
            {part.canAdd && (
              <button
                type="button"
                className="s3-pill s3-pill--sm s3-prop__add"
                disabled={busy || inviteOpen}
                aria-label="Invita nel ticket"
                onClick={() => setInviteOpen(true)}
              >
                <UserPlus size={11} aria-hidden="true" /> Invita…
              </button>
            )}
          </div>
        </div>
      )}

      {copies.visible && (
        <div className="s3-prop__row s3-prop__row--top">
          <span className="s3-label s3-prop__k">IN COPIA</span>
          <div className="s3-prop__chips">
            {copies.items.map((c) =>
              copies.canManage ? (
                <React.Fragment key={c.id}>
                  <button
                    type="button"
                    className="s3-pill s3-pill--sm"
                    disabled={busy}
                    aria-label={`Togli ${c.nome} dalla copia`}
                    title={`${c.nome} · In copia`}
                    onClick={() => run(async () => { await api.removeCopyGroup!(ticket._id, c.id); }, 'Canale tolto dalla copia')}
                  >
                    {c.nome}
                    <X size={11} aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="s3-pill s3-pill--sm s3-prop__add"
                    disabled={busy}
                    aria-label={`Rendi ${c.nome} responsabile`}
                    onClick={() => (copies.promoteByTransfer ? onOpenTransfer(c.id) : setPromote(c))}
                  >
                    Rendi responsabile
                  </button>
                </React.Fragment>
              ) : (
                <span key={c.id} className="s3-pill s3-pill--sm s4-prop__ro" title={`${c.nome} · In copia`}>{c.nome}</span>
              )
            )}
            {copies.items.length === 0 && <span className="s3-prop__val">nessuno</span>}
            {copies.canManage && copies.options.length > 0 && (
              <select
                className="s3-sel s3-sel--prop"
                aria-label="Metti in copia un canale"
                value=""
                disabled={busy}
                onChange={(e) => {
                  const gid = e.target.value;
                  if (gid) void run(async () => { await api.addCopyGroups!(ticket._id, [gid]); }, 'Canale messo in copia');
                }}
              >
                <option value="">Aggiungi…</option>
                {copies.options.map((g) => (
                  <option key={g._id} value={g._id}>{g.nome}</option>
                ))}
              </select>
            )}
          </div>
        </div>
      )}

      {/* Ticket nel canale di smistamento: l'email di apertura e trattenuta
          fino al trasferimento (backend: requesterWelcomeSentAt). Solo nel
          predefinito e su un ticket vivo; richiedente da identificare: l'email
          parte quando viene impostato. Stesso testo nelle due vesti. */}
      {ticket.requesterWelcomePending
        && ticket.groupId?.inboundDefault === true
        && ticket.status !== 'chiuso' && ticket.status !== 'risolto' && (
        <p className="s3-prop__note" data-testid="welcome-pending">
          {ticket.inbound?.needsRequester === true
            ? "Il richiedente riceverà l'email quando verrà impostato."
            : "Il richiedente non è ancora stato avvisato: riceverà l'email di apertura al trasferimento nel canale competente."}
        </p>
      )}

      {assigneesOpen && (
        <AssigneesModal
          candidates={candidates}
          busy={busy}
          searchExternal={searchExternal}
          externalAssigned={(ticket.assignees || []).filter((a) => a.external)}
          onClose={() => setAssigneesOpen(false)}
          onSave={async (payload) => {
            setAssigneesOpen(false);
            await run(async () => { await api.setAssignees(ticket._id, payload); }, 'Componenti aggiornati');
          }}
        />
      )}

      {inviteOpen && api.searchInvitable && (
        <InviteParticipantModal
          busy={busy}
          search={(q, page) => api.searchInvitable!(ticket._id, q, page)}
          onClose={() => setInviteOpen(false)}
          onPick={(c) => {
            setInviteOpen(false);
            void run(
              async () => { await api.addParticipants!(ticket._id, [{ actorType: c.actorType, actorId: c.actorId }]); },
              'Partecipante invitato'
            );
          }}
        />
      )}

      {promote && (
        <S3Modal open onClose={() => setPromote(null)} labelledBy="s3-promote-title">
          <div className="s3-mod">
            <div className="s3-mod__head">
              <h3 className="s3-h3" id="s3-promote-title">Rendi responsabile {promote.nome}</h3>
            </div>
            <div className="s3-mod__body">
              <p className="s3-mod__note">
                Il ticket passa al canale {promote.nome}: gli assegnatari si azzerano, il canale riceve l'avviso
                e {ticket.groupId?.nome || 'il canale attuale'} scende in copia.
              </p>
            </div>
            <div className="s3-mod__foot">
              <button type="button" className="s3-pill" onClick={() => setPromote(null)}>Annulla</button>
              <button
                type="button"
                className="s3-pill s3-pill--primary"
                disabled={busy}
                onClick={() => {
                  const c = promote;
                  setPromote(null);
                  void run(async () => { await api.setResponsible!(ticket._id, c.id); }, `Responsabile: ${c.nome}`);
                }}
              >
                Conferma
              </button>
            </div>
          </div>
        </S3Modal>
      )}
    </div>
  );
};

export default PropertiesCard;
